import _tcLib from "../../lib/timecode.js";
import { v4 as _uuidv } from "uuid";
const uuidv4 = _uuidv;
const tcLib = _tcLib;

/** Embedded Advantage header template (from final-test-file.ESF) so dist builds need no sidecar bins. */
const HEADER_TEMPLATE_B64 = "AACNAQAA//50AGUAcwB0ACAAMwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAR0JSIFN0YXJmaXNoQQBEAHYAbgB0AGEAZwBlADIAMAAxADgAMAA4ADEANgAxADEAMQA2ADIANABHQlIgU3RhcmZpc2hBAEQAdgBuAHQAYQBnAGUAMgAwADEAOAAwADgAMQA2ADEAMQAxADYAMgA0AAAAAAAAAAAAAAAAAAAAAAAAAAAAMAIAPgIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAR0JSIFN0YXJmaXNoQQBEAHYAbgB0AGEAZwBlAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAR0JSIFN0YXJmaXNoQQBEAHYAbgB0AGEAZwBlAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABALwDAAB0AGUAcwB0ACAAMwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACogICAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAR0JSIFN0YXJmaXNoQQBEAHYAbgB0AGEAZwBlADIAMAAxADgAMAA4ADEANgAxADEAMQA2ADIANAAAAAAAAAAAAAAAAAAAAAAA";
const GROUP_PREAMBLE_B64 = "AABkAAYAAABnACQBAAD//////////w==";

/** Default fade lead/trail used by the Starfish Advantage sample (frames). */
const DEFAULT_FADE_FRAMES = 3;

/**
 * Format seconds as packed HHMMSSFF ASCII (no separators).
 */
function secToPackedTc(sec, frameRate, dropFrame) {
  const tc = tcLib.secToTc(sec, frameRate, dropFrame || false);
  return String(tc).replace(/[:;.]/g, "");
}

/**
 * Parse packed HHMMSSFF ASCII to seconds.
 */
function packedTcToSec(packed, frameRate, dropFrame) {
  const tc = `${packed.slice(0, 2)}:${packed.slice(2, 4)}:${packed.slice(4, 6)}:${packed.slice(6, 8)}`;
  return tcLib.tcToSec(tc, frameRate, dropFrame || false);
}
function formatPackedTc(packed) {
  return `${packed.slice(0, 2)}:${packed.slice(2, 4)}:${packed.slice(4, 6)}:${packed.slice(6, 8)}`;
}
function readAscii(buf, off, n) {
  return buf.slice(off, off + n).toString("ascii");
}
function writeUtf16Le(str) {
  const out = Buffer.alloc(str.length * 2);
  for (let i = 0; i < str.length; i++) {
    out.writeUInt16LE(str.charCodeAt(i), i * 2);
  }
  return out;
}
function readUtf16Chars(buf, off, charCount) {
  let s = "";
  for (let i = 0; i < charCount; i++) {
    s += String.fromCharCode(buf.readUInt16LE(off + i * 2));
  }
  return s;
}

/**
 * Read a null-terminated UTF-16LE string from a fixed field starting at off.
 */
function readUtf16zField(buf, off, maxBytes) {
  let s = "";
  const limit = Math.min(buf.length, off + maxBytes);
  for (let o = off; o + 1 < limit; o += 2) {
    const c = buf.readUInt16LE(o);
    if (c === 0) break;
    s += String.fromCharCode(c);
  }
  return s;
}

/**
 * Patch a fixed UTF-16LE title field (null-terminated, remainder zeroed).
 */
function patchUtf16zField(buf, off, maxBytes, title) {
  const field = Buffer.alloc(maxBytes, 0);
  const chars = Math.min(title.length, Math.floor(maxBytes / 2) - 1);
  for (let i = 0; i < chars; i++) {
    field.writeUInt16LE(title.charCodeAt(i), i * 2);
  }
  field.copy(buf, off);
}
function findStarMarkers(buf) {
  const markers = [];
  for (let i = 0; i < buf.length - 2; i++) {
    // ASCII '*' followed by UTF-16LE '{'
    if (buf[i] === 0x2a && buf[i + 1] === 0x7b && buf[i + 2] === 0x00) {
      markers.push(i);
    }
  }
  return markers;
}

/**
 * Parse an ESEF (.esf) binary buffer.
 * @returns {{ title: string, authorship: string, fileIn: string, fileOut: string, headerRaw: Buffer, groupPreamble: Buffer, cues: object[] }}
 */
function parseEsf(input) {
  const buf = Buffer.isBuffer(input) ? input : Buffer.from(input);
  const markers = findStarMarkers(buf);
  if (markers.length === 0) {
    throw new Error("ESEF: no description cues found (missing '*' + WAV markers)");
  }

  // title after UTF-16LE BOM (FF FE)
  const title = buf[6] === 0xff && buf[7] === 0xfe ? readUtf16zField(buf, 8, 256) : readUtf16zField(buf, 8, 256);

  // Authorship near first "GBR StarfishA" hit
  let authorship = "";
  const gbr = buf.indexOf("GBR StarfishA");
  if (gbr >= 0) {
    const asciiPart = readAscii(buf, gbr, 13); // "GBR StarfishA"
    const rest = readUtf16zField(buf, gbr + 13, 64);
    authorship = asciiPart + rest;
  }
  const cues = [];
  for (let i = 0; i < markers.length; i++) {
    const mk = markers[i];
    const fadeIn = readAscii(buf, mk - 16, 8);
    const fadeOut = readAscii(buf, mk - 8, 8);
    if (!/^[0-9]{8}$/.test(fadeIn) || !/^[0-9]{8}$/.test(fadeOut)) {
      throw new Error(`ESEF: invalid fade timecode before cue ${i + 1}`);
    }
    let o = mk + 1;
    let wav = "";
    while (o + 1 < buf.length) {
      const c = buf.readUInt16LE(o);
      if (c === 0) break;
      wav += String.fromCharCode(c);
      o += 2;
      if (wav.toLowerCase().endsWith(".wav")) break;
    }
    if (buf[o] !== 0x02) {
      throw new Error(`ESEF: expected 0x02 before audio-in at cue ${i + 1}`);
    }
    const audioIn = readAscii(buf, o + 1, 8);
    if (buf[o + 9] !== 0x24) {
      throw new Error(`ESEF: expected '$' after audio-in at cue ${i + 1}`);
    }
    if (buf[o + 11] !== 0x03) {
      throw new Error(`ESEF: expected 0x03 before audio-out at cue ${i + 1}`);
    }
    const audioOut = readAscii(buf, o + 12, 8);
    const trailOff = o + 20;
    let trailer = Buffer.alloc(0);
    let trailerExtra = 0;
    let cueIndex = i + 1;
    // Full trailer: 00 00 03 67 00 + u16 + index + 8×FF = 17 bytes
    if (trailOff + 17 <= buf.length && buf[trailOff] === 0x00 && buf[trailOff + 1] === 0x00 && buf[trailOff + 2] === 0x03 && buf[trailOff + 3] === 0x67) {
      trailer = Buffer.from(buf.slice(trailOff, trailOff + 17));
      trailerExtra = buf.readUInt16LE(trailOff + 5);
      cueIndex = buf.readUInt16LE(trailOff + 7);
    } else if (trailOff + 3 <= buf.length && buf[trailOff] === 0x00 && buf[trailOff + 1] === 0x00 && buf[trailOff + 2] === 0x03) {
      // Truncated last-cue trailer seen in Advantage sample: 00 00 03
      trailer = Buffer.from(buf.slice(trailOff, trailOff + 3));
    }
    const textEnd = mk - 16;
    let overallOff = -1;
    for (let j = textEnd; j >= 16; j--) {
      const cand = readAscii(buf, j - 16, 16);
      if (/^[0-9]{16}$/.test(cand) && j - 16 !== mk - 16) {
        overallOff = j - 16;
        break;
      }
    }
    if (overallOff < 0) {
      throw new Error(`ESEF: could not locate overall timecode for cue ${i + 1}`);
    }
    const overallIn = readAscii(buf, overallOff, 8);
    const overallOut = readAscii(buf, overallOff + 8, 8);
    const charCount = buf.readUInt16LE(overallOff + 16);
    const text = readUtf16Chars(buf, overallOff + 18, charCount);
    cues.push({
      index: cueIndex,
      overallIn,
      overallOut,
      fadeIn,
      fadeOut,
      audioIn,
      audioOut,
      text,
      wav,
      trailerExtra,
      trailer,
      overallInDisplay: formatPackedTc(overallIn),
      overallOutDisplay: formatPackedTc(overallOut),
      fadeInDisplay: formatPackedTc(fadeIn),
      fadeOutDisplay: formatPackedTc(fadeOut),
      audioInDisplay: formatPackedTc(audioIn),
      audioOutDisplay: formatPackedTc(audioOut)
    });
  }

  // File-level range sits immediately before the first cue's overall TC block,
  // after a group preamble. Locate via first cue overall offset.
  const firstOverall = (() => {
    const mk = markers[0];
    const textEnd = mk - 16;
    for (let j = textEnd; j >= 16; j--) {
      const cand = readAscii(buf, j - 16, 16);
      if (/^[0-9]{16}$/.test(cand) && j - 16 !== mk - 16) return j - 16;
    }
    return -1;
  })();

  // Walk back: group preamble ends with 8×FF, then optional 00 00 prefix;
  // before that: 32 ASCII digits (fileIn + fileOut + 16 zeros).
  let fileIn = cues[0].overallIn;
  let fileOut = cues[cues.length - 1].overallOut;
  let headerRaw = Buffer.alloc(0);
  let groupPreamble = Buffer.alloc(0);
  if (firstOverall > 32) {
    // Scan backward for 32 ASCII digits ending just before preamble
    for (let end = firstOverall; end >= 32; end--) {
      const cand = readAscii(buf, end - 32, 32);
      if (/^[0-9]{32}$/.test(cand)) {
        fileIn = cand.slice(0, 8);
        fileOut = cand.slice(8, 16);
        headerRaw = Buffer.from(buf.slice(0, end - 32));
        groupPreamble = Buffer.from(buf.slice(end - 32 + 32, firstOverall));
        break;
      }
    }
  }
  if (headerRaw.length === 0) {
    headerRaw = Buffer.from(buf.slice(0, Math.max(0, firstOverall - 54)));
    groupPreamble = loadGroupPreamble();
  }
  return {
    title,
    authorship,
    fileIn,
    fileOut,
    fileInDisplay: formatPackedTc(fileIn),
    fileOutDisplay: formatPackedTc(fileOut),
    headerRaw,
    groupPreamble,
    cues
  };
}
function loadHeaderTemplate() {
  return Buffer.from(HEADER_TEMPLATE_B64, "base64");
}
function loadGroupPreamble() {
  return Buffer.from(GROUP_PREAMBLE_B64, "base64");
}

/**
 * Build cue body without trailing size/index footer.
 * Footer is appended separately so next-cue sizes can be filled in a second pass.
 */
function buildCueBody(cue) {
  const parts = [];
  parts.push(Buffer.from(cue.overallIn + cue.overallOut, "ascii"));
  const lenBuf = Buffer.alloc(2);
  lenBuf.writeUInt16LE(cue.text.length, 0);
  parts.push(lenBuf);
  parts.push(writeUtf16Le(cue.text));
  parts.push(Buffer.from(cue.fadeIn + cue.fadeOut, "ascii"));
  parts.push(Buffer.from([0x2a])); // '*'
  parts.push(writeUtf16Le(cue.wav));
  parts.push(Buffer.from([0x02]));
  parts.push(Buffer.from(cue.audioIn, "ascii"));
  parts.push(Buffer.from([0x24, 0x00, 0x03])); // '$' 0x00 0x03
  parts.push(Buffer.from(cue.audioOut, "ascii"));
  return Buffer.concat(parts);
}

/**
 * Full trailer (17 bytes): 00 00 03 'g' 00 | nextCueSize(u16) | cueIndex(u16) | FF×8
 * Truncated last-cue trailer (3 bytes): 00 00 03
 */
function buildCueTrailer(index, nextCueSize, {
  truncated = false
} = {}) {
  if (truncated) {
    return Buffer.from([0x00, 0x00, 0x03]);
  }
  const full = Buffer.alloc(17, 0xff);
  full[0] = 0x00;
  full[1] = 0x00;
  full[2] = 0x03;
  full[3] = 0x67;
  full[4] = 0x00;
  full.writeUInt16LE(nextCueSize || 0, 5);
  full.writeUInt16LE(index, 7);
  return full;
}

/**
 * Build a cue binary record.
 * @param {number} nextCueSize Byte length of the following cue (as stored in Advantage files).
 */
function buildCue(cue, index, {
  includeFullTrailer = true,
  nextCueSize = 0
} = {}) {
  const body = buildCueBody(cue);
  const trailer = buildCueTrailer(index, nextCueSize || cue.trailerExtra || 0, {
    truncated: !includeFullTrailer
  });
  return Buffer.concat([body, trailer]);
}

/**
 * Patch group preamble first-cue size field (u16le at offset 10).
 */
function patchPreambleFirstCueSize(preamble, firstCueSize) {
  const out = Buffer.from(preamble);
  if (out.length >= 12 && out[8] === 0x67) {
    out.writeUInt16LE(firstCueSize, 10);
  }
  return out;
}

/**
 * Build an ESEF binary buffer from parsed/constructed data.
 */
function buildEsf({
  title,
  cues,
  fileIn,
  fileOut,
  headerRaw,
  groupPreamble
}, options = {}) {
  if (!cues || cues.length === 0) {
    throw new Error("ESEF: cannot encode empty event list");
  }
  let header = headerRaw && headerRaw.length ? Buffer.from(headerRaw) : loadHeaderTemplate();
  let preamble = groupPreamble && groupPreamble.length ? Buffer.from(groupPreamble) : loadGroupPreamble();
  const docTitle = title != null && title !== "" ? String(title) : "Untitled";
  // Patch known title slots in Advantage header template
  if (header.length >= 8 + 256) {
    // UTF-16LE BOM is bytes FF FE (not UInt16LE 0xFFFE which writes FE FF)
    header[6] = 0xff;
    header[7] = 0xfe;
    patchUtf16zField(header, 8, 256, docTitle);
  }
  if (header.length >= 977 + 64) {
    patchUtf16zField(header, 977, 64, docTitle);
  }
  const resolvedFileIn = fileIn || cues[0].overallIn;
  const resolvedFileOut = fileOut || cues[cues.length - 1].overallOut;
  const fileTcBlock = Buffer.from(resolvedFileIn + resolvedFileOut + "0000000000000000", "ascii");

  // Two-pass sizes: bodies first, then trailers that reference next cue total size
  const bodies = cues.map(cue => buildCueBody(cue));
  const fullLast = !!options.fullLastTrailer;
  const trailerLens = cues.map((_, i) => {
    const isLast = i === cues.length - 1;
    return isLast && !fullLast ? 3 : 17;
  });
  const totalSizes = bodies.map((b, i) => b.length + trailerLens[i]);

  // For non-last cues, trailer stores the following cue's total size.
  // Last full trailer (rare) stores 0.
  const cueBufs = bodies.map((body, i) => {
    const isLast = i === cues.length - 1;
    const nextSize = isLast ? 0 : totalSizes[i + 1];
    // When last cue is truncated, Advantage still stores next-size in prior cue
    // as if the last cue had a full 17-byte trailer:
    const storedNext = !isLast && i + 1 === cues.length - 1 && !fullLast ? bodies[i + 1].length + 17 : nextSize;
    const trailer = buildCueTrailer(i + 1, isLast ? 0 : storedNext, {
      truncated: isLast && !fullLast
    });
    return Buffer.concat([body, trailer]);
  });
  preamble = patchPreambleFirstCueSize(preamble, totalSizes[0]);
  return Buffer.concat([header, fileTcBlock, preamble, ...cueBufs]);
}

/**
 * Derive packed fade/audio windows from overall in/out using N-frame lead/trail.
 */
function deriveWindows(overallInSec, overallOutSec, frameRate, dropFrame, fadeFrames = DEFAULT_FADE_FRAMES) {
  const frameSec = 1 / parseFloat(frameRate);
  const fadeStart = overallInSec + fadeFrames * frameSec;
  const fadeEnd = overallOutSec - fadeFrames * frameSec;
  const audioStart = overallInSec;
  const audioEnd = fadeEnd;
  return {
    overallIn: secToPackedTc(overallInSec, frameRate, dropFrame),
    overallOut: secToPackedTc(overallOutSec, frameRate, dropFrame),
    fadeIn: secToPackedTc(Math.min(fadeStart, fadeEnd), frameRate, dropFrame),
    fadeOut: secToPackedTc(Math.max(fadeStart, fadeEnd), frameRate, dropFrame),
    audioIn: secToPackedTc(audioStart, frameRate, dropFrame),
    audioOut: secToPackedTc(audioEnd, frameRate, dropFrame)
  };
}
function ensureWavName(name) {
  if (name && String(name).trim()) {
    const trimmed = String(name).trim();
    const parts = trimmed.split(/[\\/]/);
    const base = parts[parts.length - 1] || trimmed;
    return /\.wav$/i.test(base) ? base : `${base}.wav`;
  }
  return `{${uuidv4().toUpperCase()}}.wav`;
}
export default {
  DEFAULT_FADE_FRAMES,
  parseEsf,
  buildEsf,
  buildCue,
  buildCueBody,
  buildCueTrailer,
  secToPackedTc,
  packedTcToSec,
  formatPackedTc,
  deriveWindows,
  ensureWavName,
  loadHeaderTemplate,
  loadGroupPreamble
};